import { useRef, useState, useEffect, useCallback } from 'react';
import { Group, ActionIcon, Tooltip, Stack } from '@mantine/core';
import {
  IconPencil,
  IconArrowUpRight,
  IconSquare,
  IconTypography,
  IconArrowBackUp,
} from '@tabler/icons-react';

type Tool = 'draw' | 'arrow' | 'highlight' | 'text';

interface Annotation {
  type: Tool;
  points: { x: number; y: number }[];
  text?: string;
}

interface AnnotationCanvasProps {
  imageBlob: Blob;
  onExport: (annotatedBlob: Blob) => void;
  width?: number;
  height?: number;
}

export function AnnotationCanvas({ imageBlob, onExport, width = 800, height = 600 }: AnnotationCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = useState<Tool>('draw');
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null);
  const [canvasSize, setCanvasSize] = useState({ w: width, h: height });

  // Load background image
  useEffect(() => {
    const url = URL.createObjectURL(imageBlob);
    const img = new Image();
    img.onload = () => {
      const aspect = img.width / img.height;
      const w = Math.min(width, img.width);
      const h = Math.max(w / aspect, 100);
      setCanvasSize({ w, h });
      setBgImage(img);
    };
    img.onerror = () => {
      console.error('Failed to load screenshot blob as image');
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [imageBlob, width]);

  // Redraw canvas whenever annotations change
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bgImage) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bgImage, 0, 0, canvas.width, canvas.height);

    const allAnnotations = currentAnnotation
      ? [...annotations, currentAnnotation]
      : annotations;

    for (const ann of allAnnotations) {
      drawAnnotation(ctx, ann);
    }
  }, [bgImage, annotations, currentAnnotation]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  // Export annotated image
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const doExport = () => {
      canvas.toBlob((blob) => {
        if (blob) onExport(blob);
      }, 'image/png');
    };

    // Export after each annotation change (debounced)
    const timer = setTimeout(doExport, 300);
    return () => clearTimeout(timer);
  }, [annotations, onExport]);

  function getCanvasPos(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  function handleMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    const pos = getCanvasPos(e);

    if (tool === 'text') {
      const text = prompt('Enter annotation text:');
      if (text) {
        setAnnotations((prev) => [...prev, { type: 'text', points: [pos], text }]);
      }
      return;
    }

    setIsDrawing(true);
    setCurrentAnnotation({ type: tool, points: [pos] });
  }

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!isDrawing || !currentAnnotation) return;
    const pos = getCanvasPos(e);

    if (tool === 'draw') {
      setCurrentAnnotation((prev) =>
        prev ? { ...prev, points: [...prev.points, pos] } : null
      );
    } else {
      // Arrow and highlight: update second point
      setCurrentAnnotation((prev) =>
        prev ? { ...prev, points: [prev.points[0], pos] } : null
      );
    }
  }

  function handleMouseUp() {
    if (!isDrawing || !currentAnnotation) return;
    setIsDrawing(false);

    if (currentAnnotation.points.length >= 2 || currentAnnotation.type === 'draw') {
      setAnnotations((prev) => [...prev, currentAnnotation]);
    }
    setCurrentAnnotation(null);
  }

  function undo() {
    setAnnotations((prev) => prev.slice(0, -1));
  }

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Tooltip label="Freehand draw">
          <ActionIcon
            variant={tool === 'draw' ? 'filled' : 'light'}
            color="red"
            onClick={() => setTool('draw')}
          >
            <IconPencil size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Arrow">
          <ActionIcon
            variant={tool === 'arrow' ? 'filled' : 'light'}
            color="blue"
            onClick={() => setTool('arrow')}
          >
            <IconArrowUpRight size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Highlight area">
          <ActionIcon
            variant={tool === 'highlight' ? 'filled' : 'light'}
            color="yellow"
            onClick={() => setTool('highlight')}
          >
            <IconSquare size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Add text">
          <ActionIcon
            variant={tool === 'text' ? 'filled' : 'light'}
            color="green"
            onClick={() => setTool('text')}
          >
            <IconTypography size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Undo">
          <ActionIcon
            variant="light"
            color="gray"
            onClick={undo}
            disabled={annotations.length === 0}
          >
            <IconArrowBackUp size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <canvas
        ref={canvasRef}
        width={canvasSize.w}
        height={canvasSize.h}
        style={{
          border: '1px solid var(--mantine-color-gray-4)',
          borderRadius: 'var(--mantine-radius-sm)',
          cursor: tool === 'text' ? 'text' : 'crosshair',
          maxWidth: '100%',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
    </Stack>
  );
}

function drawAnnotation(ctx: CanvasRenderingContext2D, ann: Annotation) {
  switch (ann.type) {
    case 'draw':
      drawFreehand(ctx, ann.points);
      break;
    case 'arrow':
      if (ann.points.length >= 2) {
        drawArrow(ctx, ann.points[0], ann.points[ann.points.length - 1]);
      }
      break;
    case 'highlight':
      if (ann.points.length >= 2) {
        drawHighlight(ctx, ann.points[0], ann.points[ann.points.length - 1]);
      }
      break;
    case 'text':
      if (ann.text && ann.points.length >= 1) {
        drawText(ctx, ann.points[0], ann.text);
      }
      break;
  }
}

function drawFreehand(ctx: CanvasRenderingContext2D, points: { x: number; y: number }[]) {
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.strokeStyle = '#e03131';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();
}

function drawArrow(ctx: CanvasRenderingContext2D, from: { x: number; y: number }, to: { x: number; y: number }) {
  const headLen = 15;
  const angle = Math.atan2(to.y - from.y, to.x - from.x);

  ctx.beginPath();
  ctx.strokeStyle = '#1c7ed6';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();

  // Arrowhead
  ctx.beginPath();
  ctx.fillStyle = '#1c7ed6';
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - headLen * Math.cos(angle - Math.PI / 6), to.y - headLen * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(to.x - headLen * Math.cos(angle + Math.PI / 6), to.y - headLen * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
}

function drawHighlight(ctx: CanvasRenderingContext2D, from: { x: number; y: number }, to: { x: number; y: number }) {
  const x = Math.min(from.x, to.x);
  const y = Math.min(from.y, to.y);
  const w = Math.abs(to.x - from.x);
  const h = Math.abs(to.y - from.y);

  ctx.fillStyle = 'rgba(255, 224, 102, 0.35)';
  ctx.fillRect(x, y, w, h);

  ctx.strokeStyle = '#fab005';
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
}

function drawText(ctx: CanvasRenderingContext2D, pos: { x: number; y: number }, text: string) {
  ctx.font = 'bold 16px sans-serif';
  ctx.fillStyle = '#2b8a3e';

  // Background for readability
  const metrics = ctx.measureText(text);
  const padding = 4;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.fillRect(pos.x - padding, pos.y - 16 - padding, metrics.width + padding * 2, 20 + padding * 2);

  ctx.fillStyle = '#2b8a3e';
  ctx.fillText(text, pos.x, pos.y);
}
